import { IconFont, Tooltip } from '@byecode/ui'
import type { NodeTypes, SenderAccountType } from '@lighthouse/shared'
import type { HandleProps, ReactFlowState } from '@xyflow/react'
import { Handle, Position, useStore } from '@xyflow/react'
import cls from 'classnames'
import { find } from 'rambda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { useFlow, useFlowContainerContext } from '@/contexts/FlowContext'

import { useInteraction } from '../../hook/useInteraction'
import { NodeSelector } from '../NodeSelector'

const SCxWrapper = styled.div`
    position: absolute;
    /* right: -.6rem; */
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;

    &.node-handle-selected {
        opacity: 1;
    }
`

const SCxIcon = styled(IconFont)`
    transition: all 0.1s ease-in-out;
`

const SCxAnchorEl = styled.div``

interface NodeHandleProps extends Omit<HandleProps, 'type' | 'position'> {
    selected?: boolean
    style?: React.CSSProperties
    handleId?: string
    dragging?: boolean
}

interface SelectNode {
    val: NodeTypes
    senderEmailType?: SenderAccountType
}

export const NodeSourceHandle: React.FC<NodeHandleProps> = ({ id, selected, handleId, isValidConnection, ...rest }) => {
    const { isConnectable, style, dragging } = rest
    const { type, log } = useFlowContainerContext()
    const nodeId = id || ''
    const isHandleConnected = useStore(
        useCallback(
            (state: ReactFlowState) => {
                const connectedLen = state.connectionLookup.get(`${nodeId}-source`)
                if (!connectedLen) {
                    return false
                }
                return connectedLen.size > 0
            },
            [nodeId]
        )
    )
    const readonly = !!log
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const { handleNodeAdd } = useInteraction()
    const handleSelect = useCallback(
        ({ val, senderEmailType }: SelectNode) => {
            handleNodeAdd({
                nodeType: val,
                prevNodeId: id,
                senderEmailType,
                handleId
            })
            setOpen(false)
        },
        [handleId, handleNodeAdd, id]
    )

    const handleOpen = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        setOpen(true)
    }, [])

    return (
        <Handle
            type="source"
            position={Position.Right}
            id={handleId}
            className={cls({ connectingto: isHandleConnected })}
            style={style}
            isConnectableStart
            onClick={handleOpen}
        >
            {!readonly && (
                <>
                    <Tooltip title="点击添加节点">
                        <SCxWrapper
                            className={cls(
                                {
                                    'node-handle-selected': dragging || selected || open
                                },
                                'node-handle-wrapper'
                            )}
                        >
                            <SCxIcon size={16} onClick={handleOpen} type="CircleAdd" color="var(--color-main)" />
                        </SCxWrapper>
                    </Tooltip>
                    {/* <SCxLine className='node-handle-line' /> */}
                    {isConnectable && (
                        <NodeSelector
                            offsetOptions={{
                                mainAxis: 10
                            }}
                            target={ref.current}
                            position={Position.Right}
                            workflowType={type}
                            opened={open}
                            onSelect={handleSelect}
                            onChange={setOpen}
                        />
                    )}
                    <SCxAnchorEl ref={ref} />
                </>
            )}
        </Handle>
    )
}

export const NodeTargetHandle: React.FC<NodeHandleProps> = ({ id, selected, dragging, isValidConnection }) => {
    const nodeId = id || ''
    const { type, log } = useFlowContainerContext()
    const readonly = !!log
    const { nodes, edges } = useFlow()
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const isConnectable = useMemo(() => {
        const edge = find(item => item.target === id, edges)
        return !edge
    }, [edges, id])
    const isHandleConnected = useStore(
        useCallback(
            (state: ReactFlowState) => {
                const connectedLen = state.connectionLookup.get(`${nodeId}-target`)
                if (!connectedLen) {
                    return false
                }
                return connectedLen.size > 0
            },
            [nodeId]
        )
    )

    const { handleNodeAdd } = useInteraction()
    const handleSelect = useCallback(
        ({ val, senderEmailType }: SelectNode) => {
            handleNodeAdd({
                nodeType: val,
                senderEmailType,
                nextNodeId: id
            })
            setOpen(false)
        },
        [handleNodeAdd, id]
    )

    const handleOpen = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            if (!isConnectable) {
                return
            }
            setOpen(true)
        },
        [isConnectable]
    )

    return (
        <>
            <Handle
                type="target"
                className={cls({ connectingto: isHandleConnected })}
                position={Position.Left}
                isConnectableStart={isConnectable}
                onClick={handleOpen}
            >
                {!readonly && isConnectable && (
                    <>
                        <Tooltip title="点击添加节点">
                            <SCxWrapper
                                className={cls(
                                    {
                                        'node-handle-selected': selected || open || dragging
                                    },
                                    'node-handle-wrapper'
                                )}
                            >
                                <SCxIcon size={16} type="CircleAdd" color="var(--color-main)" />
                            </SCxWrapper>
                        </Tooltip>
                        <NodeSelector
                            offsetOptions={{
                                mainAxis: 12
                            }}
                            target={ref.current}
                            position={Position.Left}
                            workflowType={type}
                            opened={open}
                            onSelect={handleSelect}
                            onChange={setOpen}
                        />
                        <SCxAnchorEl ref={ref} />
                    </>
                )}
            </Handle>
        </>
    )
}
